import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DEAL_RESULT_MAP, DEAL_STAGE_MAP } from "@goyone/shared";
import { api } from "../../lib/api";
import { date, money } from "../../lib/format";
import { Badge, Modal } from "../../components/ui";
import { EntityRating } from "../../components/RatingRequest";
import { OwnerChip } from "../../components/People";

/**
 * تفاصيل الصفقة — طبقةٌ تُفتح من صفّها.
 *
 * لا صفحة مستقلة لها: الصفقة تُقرأ في سياق قائمتها، والطبقة تُبقي القائمة
 * خلفها فيعود المندوب إلى صفٍّ آخر بنقرة. تجمع ما يشرح الصفقة — مرحلتها
 * وقيمتها ومن يملكها — وما تفرّع عنها: عروض الأسعار والفواتير.
 */

interface DealFull {
  id: string;
  title: string;
  stage: keyof typeof DEAL_STAGE_MAP;
  result: keyof typeof DEAL_RESULT_MAP;
  value: string;
  probability: number;
  expectedCloseAt: string | null;
  closedAt: string | null;
  lostReason: string | null;
  ownerId: string | null;
  createdAt: string;
  account: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
  } | null;
  lead?: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
  } | null;
  quotations: Array<{
    id: string;
    number: string;
    status: string;
    total: string;
  }>;
  invoices: Array<{
    id: string;
    number: string;
    status: string;
    total: string;
    paidAmount: string;
  }>;
}

export function DealDetail({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) {
  const deal = useQuery({
    queryKey: ["deal", id],
    queryFn: () => api<DealFull>(`/sales/deals/${id}`),
  });

  const d = deal.data;

  return (
    <Modal title={d?.title ?? "الصفقة"} onClose={onClose} wide>
      {deal.isPending && <div className="skeleton" style={{ height: 220 }} />}

      {d && (
        <>
          <div className="deal-detail-head">
            <Badge kind={DEAL_RESULT_MAP[d.result]?.kind}>
              {DEAL_RESULT_MAP[d.result]?.label}
            </Badge>
            <span className="cell-mute">
              {DEAL_STAGE_MAP[d.stage]?.label ?? d.stage}
            </span>
            <span
              className="deal-detail-value num"
              style={{ marginInlineStart: "auto" }}
            >
              {money(d.value)}
            </span>
          </div>

          {/*
            رأي العميل وسببه — كسبًا وخسارةً سواء: الخاسرة أوْلى بالسؤال،
            فسببُ خسارة تعاقدٍ معلومةٌ تُصلح البيع كله لا هذه الصفقة.
          */}
          <div className="mt-3">
            <h3 className="label mb-2">تقييم العميل وسببه</h3>
            <EntityRating kind="DEAL" entityId={d.id} />
          </div>

          <div className="form-grid mt-3">
            <div className="field">
              <span className="label">الحساب</span>
              {d.account ? (
                <Link to={`/crm/accounts/${d.account.id}`} className="link">
                  {d.account.name}
                </Link>
              ) : d.lead ? (
                <Link to={`/crm/leads/${d.lead.id}`} className="link">
                  {d.lead.name} — عميل محتمل
                </Link>
              ) : (
                <span>—</span>
              )}
            </div>
            <div className="field">
              <span className="label">المسؤول</span>
              <OwnerChip ownerId={d.ownerId} size={22} />
            </div>
            <div className="field">
              <span className="label">الاحتمالية</span>
              <span className="num">{d.probability}٪</span>
            </div>
            <div className="field">
              <span className="label">الإغلاق المتوقّع</span>
              <span>{d.expectedCloseAt ? date(d.expectedCloseAt) : "—"}</span>
            </div>
            {d.closedAt && (
              <div className="field">
                <span className="label">أُغلقت في</span>
                <span>{date(d.closedAt)}</span>
              </div>
            )}
            <div className="field">
              <span className="label">أُنشئت في</span>
              <span>{date(d.createdAt)}</span>
            </div>
          </div>

          {d.result === "LOST" && d.lostReason && (
            <div className="alert alert-danger mt-3">
              سبب الخسارة: <strong>{d.lostReason}</strong>
            </div>
          )}

          {/* ما تفرّع عن الصفقة — عروضٌ وفواتير، كلٌّ رابطٌ إلى مستنده. */}
          {d.quotations.length > 0 && (
            <div className="mt-4">
              <h4 className="section-title">
                عروض الأسعار ({d.quotations.length})
              </h4>
              <div className="col" style={{ gap: 4 }}>
                {d.quotations.map((q) => (
                  <Link
                    key={q.id}
                    to={`/sales/quotations/${q.id}`}
                    className="deal-doc-row"
                  >
                    <span className="num">{q.number}</span>
                    <Badge kind="neutral">{q.status}</Badge>
                    <span className="num" style={{ marginInlineStart: "auto" }}>
                      {money(q.total)}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {d.invoices.length > 0 && (
            <div className="mt-4">
              <h4 className="section-title">الفواتير ({d.invoices.length})</h4>
              <div className="col" style={{ gap: 4 }}>
                {d.invoices.map((inv) => (
                  <div key={inv.id} className="deal-doc-row">
                    <span className="num">{inv.number}</span>
                    <Badge kind="neutral">{inv.status}</Badge>
                    <span className="tiny mute num">
                      حُصّل {money(inv.paidAmount)} من {money(inv.total)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
